import { useEffect, useState, type FormEvent } from 'react';
import {
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  Key,
  KeyRound,
  Lock,
  Pencil,
  Plus,
  StickyNote,
  Terminal,
  Trash2,
  Wifi,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { DataList, DataListRow } from '@/components/data-list';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Menu } from '@/components/menu';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { useToast } from '@/components/toast';
import {
  useCreateCredential,
  useCredentials,
  useDeleteCredential,
  useRevealCredential,
  useUpdateCredential,
  type CredentialInput,
} from '@/lib/credentials';
import { useFormError } from '@/lib/form-error';
import type { CredentialData, CredentialRevealedData } from '@/types/api';

const TYPE_META: Record<
  string,
  { label: string; icon: LucideIcon; valueLabel: string }
> = {
  password: { label: 'Password', icon: KeyRound, valueLabel: 'Password' },
  api_key: { label: 'API key', icon: Key, valueLabel: 'Key' },
  ssh_key: { label: 'SSH key', icon: Terminal, valueLabel: 'Private key' },
  license_key: { label: 'License key', icon: Lock, valueLabel: 'License key' },
  wifi: { label: 'Wi-Fi', icon: Wifi, valueLabel: 'Passphrase' },
  note: { label: 'Secure note', icon: StickyNote, valueLabel: 'Content' },
};

const TYPE_OPTIONS = Object.entries(TYPE_META).map(([value, m]) => ({ value, label: m.label }));

function metaFor(type: string) {
  return TYPE_META[type] ?? { label: type, icon: KeyRound, valueLabel: 'Value' };
}

export function ClientVault({ clientId }: { clientId: number }) {
  const { data, isLoading } = useCredentials(clientId);
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [revealedId, setRevealedId] = useState<number | null>(null);

  const editing =
    editingId != null ? (data ?? []).find((c) => c.id === editingId) ?? null : null;
  const deleting =
    deletingId != null ? (data ?? []).find((c) => c.id === deletingId) ?? null : null;

  const create = useCreateCredential(clientId);
  const remove = useDeleteCredential(clientId);

  return (
    <div className="space-y-4">
      {data && data.length > 0 ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {data.length} {data.length === 1 ? 'credential' : 'credentials'} · Encrypted at rest · Every reveal is logged
          </p>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Add credential
          </Button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No credentials yet"
          description="Store passwords, API keys, SSH keys, Wi-Fi passphrases, and secure notes for this client. Everything is encrypted at rest and access is audit-logged."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Add credential
            </Button>
          }
        />
      ) : (
        <DataList>
          {data.map((c) => (
            <CredentialRow
              key={c.id}
              clientId={clientId}
              credential={c}
              onReveal={() => setRevealedId(c.id)}
              onEdit={() => setEditingId(c.id)}
              onDelete={() => setDeletingId(c.id)}
            />
          ))}
        </DataList>
      )}

      <CredentialDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add credential"
        submitLabel="Add"
        onSubmit={async (input) => {
          await create.mutateAsync(input);
          toast.success('Credential added');
          setCreateOpen(false);
        }}
        clientId={clientId}
        mode="create"
      />

      {editing ? (
        <EditCredentialDialog
          credential={editing}
          clientId={clientId}
          onDone={() => setEditingId(null)}
        />
      ) : null}

      {revealedId != null ? (
        <RevealDialog
          clientId={clientId}
          credentialId={revealedId}
          onClose={() => setRevealedId(null)}
        />
      ) : null}

      <Dialog
        open={deleting != null}
        onClose={() => setDeletingId(null)}
        title={deleting ? `Delete ${deleting.label}?` : ''}
      >
        <p className="text-sm text-muted-foreground">
          This credential will be removed from the vault. You won't be able to retrieve it without
          re-entering the value.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeletingId(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={async () => {
              if (!deleting) return;
              await remove.mutateAsync(deleting.id);
              toast.success(`${deleting.label} removed`);
              setDeletingId(null);
            }}
          >
            {remove.isPending ? 'Deleting…' : 'Delete credential'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function CredentialRow({
  credential,
  onReveal,
  onEdit,
  onDelete,
}: {
  clientId: number;
  credential: CredentialData;
  onReveal: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const m = metaFor(credential.type);
  const Icon = m.icon;
  const toast = useToast();

  function copyUsername() {
    if (!credential.username) return;
    void navigator.clipboard.writeText(credential.username);
    toast.success('Username copied');
  }

  return (
    <DataListRow interactive={false}>
      <div className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{credential.label}</span>
          <Badge tone="neutral">{m.label}</Badge>
        </div>
        <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
          {credential.username ? (
            <button
              type="button"
              onClick={copyUsername}
              className="inline-flex items-center gap-1 hover:text-foreground"
              title="Copy username"
            >
              <span className="truncate">{credential.username}</span>
              <Copy className="size-3" />
            </button>
          ) : null}
          {credential.url ? (
            <a
              href={credential.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 truncate hover:text-foreground"
              title={credential.url}
            >
              <ExternalLink className="size-3" />
              <span className="truncate">{prettyUrl(credential.url)}</span>
            </a>
          ) : null}
          {credential.last_revealed_at ? (
            <span title={`Last revealed ${formatWhen(credential.last_revealed_at)}`}>
              Last viewed {formatWhen(credential.last_revealed_at)}
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="secondary" onClick={onReveal}>
          <Eye className="size-3.5" /> Reveal
        </Button>
        <Menu
          items={[
            { key: 'edit', label: 'Edit', icon: Pencil, onSelect: onEdit },
            { key: 'delete', label: 'Delete', icon: Trash2, danger: true, onSelect: onDelete },
          ]}
        />
      </div>
    </DataListRow>
  );
}

function RevealDialog({
  clientId,
  credentialId,
  onClose,
}: {
  clientId: number;
  credentialId: number;
  onClose: () => void;
}) {
  const reveal = useRevealCredential(clientId);
  const toast = useToast();
  const [data, setData] = useState<CredentialRevealedData | null>(null);
  const [show, setShow] = useState(false);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await reveal.mutateAsync(credentialId);
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Could not reveal credential');
      } finally {
        if (!cancelled) setPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialId]);

  function copy(value: string, label: string) {
    void navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  }

  return (
    <Dialog open={true} onClose={onClose} title={data?.label ?? 'Reveal credential'}>
      {pending ? (
        <p className="text-sm text-muted-foreground">Decrypting…</p>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : data ? (
        <div className="space-y-3">
          {data.username ? (
            <Field label="Username" value={data.username} onCopy={() => copy(data.username!, 'Username')} />
          ) : null}
          <Field
            label={metaFor(data.type).valueLabel}
            value={data.value}
            secret
            show={show}
            onToggle={() => setShow((v) => !v)}
            onCopy={() => copy(data.value, metaFor(data.type).valueLabel)}
          />
          {data.url ? (
            <div className="text-sm">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">URL</div>
              <a
                href={data.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 break-all text-primary hover:underline"
              >
                {data.url}
                <ExternalLink className="size-3" />
              </a>
            </div>
          ) : null}
          {data.notes ? (
            <div className="text-sm">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Notes</div>
              <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 px-3 py-2">
                {data.notes}
              </div>
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            This reveal was logged. Don't paste credentials into chat.
          </p>
        </div>
      ) : null}
      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Dialog>
  );
}

function Field({
  label,
  value,
  secret = false,
  show = true,
  onToggle,
  onCopy,
}: {
  label: string;
  value: string;
  secret?: boolean;
  show?: boolean;
  onToggle?: () => void;
  onCopy: () => void;
}) {
  return (
    <div>
      <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 break-all rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-sm">
          {secret && !show ? '•'.repeat(Math.min(value.length, 24)) : value}
        </div>
        {secret && onToggle ? (
          <Button variant="secondary" onClick={onToggle} aria-label={show ? 'Hide' : 'Show'}>
            {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onCopy} aria-label={`Copy ${label}`}>
          <Copy className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function EditCredentialDialog({
  credential,
  clientId,
  onDone,
}: {
  credential: CredentialData;
  clientId: number;
  onDone: () => void;
}) {
  const update = useUpdateCredential(clientId, credential.id);
  return (
    <CredentialDialog
      open={true}
      onClose={onDone}
      title="Edit credential"
      submitLabel="Save"
      clientId={clientId}
      mode="edit"
      initial={credential}
      onSubmit={async (input) => {
        await update.mutateAsync(input);
        onDone();
      }}
    />
  );
}

function CredentialDialog({
  open,
  onClose,
  title,
  submitLabel,
  initial,
  mode,
  onSubmit,
  clientId: _clientId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  submitLabel: string;
  initial?: CredentialData;
  mode: 'create' | 'edit';
  onSubmit: (input: CredentialInput) => Promise<void>;
  clientId: number;
}) {
  const [type, setType] = useState(initial?.type ?? 'password');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [username, setUsername] = useState(initial?.username ?? '');
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [pending, setPending] = useState(false);
  const { error, wrap } = useFormError();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    const input: CredentialInput = {
      type,
      label,
      url: url || null,
      username: username || null,
      value,
      notes: notes || null,
    };
    // On edit, omit value when the user didn't enter a new one so the existing
    // secret is preserved server-side.
    if (mode === 'edit' && !value) {
      delete (input as Partial<CredentialInput>).value;
    }
    const ok = await wrap(() => onSubmit(input));
    setPending(false);
    if (ok && mode === 'create') {
      setType('password');
      setLabel('');
      setUrl('');
      setUsername('');
      setValue('');
      setNotes('');
    }
  }

  const valueLabel = metaFor(type).valueLabel;

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form className="space-y-3" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-2">
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Input
            required
            placeholder="Label (e.g. Router admin)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <Input
          placeholder="Username (optional)"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Input
          placeholder="URL (optional)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Textarea
          required={mode === 'create'}
          placeholder={
            mode === 'edit'
              ? `New ${valueLabel.toLowerCase()} (leave blank to keep existing)`
              : valueLabel
          }
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="font-mono"
        />
        <Textarea
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function prettyUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '') + (u.pathname !== '/' ? u.pathname : '');
  } catch {
    return url;
  }
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return date.toLocaleDateString();
}
